import { shared } from '../config';
import { GLADLY_LOADER_SOURCE, GLADLY_SCRIPT_ID } from '../head/gladly';

const INIT_PROMISE_KEY = '__ollieGladlyInitPromise';

const installGladlyLoader = () => {
  if (window.Gladly || document.getElementById(GLADLY_SCRIPT_ID)) return;

  const script = document.createElement('script');
  script.id = GLADLY_SCRIPT_ID;
  script.text = GLADLY_LOADER_SOURCE;
  document.head.appendChild(script);
};

/**
 * Load the Gladly SDK if needed and call `init` once.
 * Safe to call from the chat button and from `useGladlyAvailability`.
 * `autoShowButton: false` keeps Gladly's own button hidden when the custom
 * chat button is not on the page.
 *
 * A rejected `init` rejects the returned promise and is forgotten, so the
 * next call can try again. Callers that share an in-flight attempt keep
 * waiting on that same promise.
 *
 * @returns {Promise<void> | null} null when Gladly or the publishable key is not ready yet
 */
export const ensureGladlyInitialized = () => {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window[INIT_PROMISE_KEY]) return window[INIT_PROMISE_KEY];

  installGladlyLoader();

  const gladly = window.Gladly;
  const appId = shared.GLADLY_PUBLISHABLE_KEY;
  if (!gladly || typeof gladly.init !== 'function' || !appId) return null;

  // The loader stub records `then` and `catch` separately and drops the
  // rejection handler passed to `then`. Adopting that return value with
  // `Promise.resolve` never hears a failed startup, so register both.
  // Only forget this attempt. A failure must not clear one that replaced it.
  const promise = new Promise((resolve, reject) => {
    const pendingInit = gladly.init({ autoShowButton: false, appId });
    if (!pendingInit || typeof pendingInit.then !== 'function') {
      resolve();
      return;
    }
    pendingInit.then(() => resolve()).catch(reject);
  }).catch(error => {
    if (window[INIT_PROMISE_KEY] === promise) {
      delete window[INIT_PROMISE_KEY];
    }
    throw error;
  });

  window[INIT_PROMISE_KEY] = promise;
  return promise;
};
